import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../services/api';
import { TermSettingsFields, termPayload } from '../components/TermSettings';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, SparklesIcon, ChevronRightIcon,
  CheckCircleIcon, TrashIcon, PencilSquareIcon, LockClosedIcon,
  PencilIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';

const fmtDate = (d) => {
  if (!d) return '—';
  const dt  = new Date(d);
  const dd  = String(dt.getDate()).padStart(2, '0');
  const mon = dt.toLocaleDateString('en-US', { month: 'short' });
  return `${dd}-${mon}-${dt.getFullYear()}`;
};
const curYear = () => { const y = new Date().getFullYear(); return `${y}-${y+1}`; };

const DIFF_LABELS  = { easy: '🟢 Easy', medium: '🟡 Medium', hard: '🔴 Hard' };
const SUBJ_COLORS  = ['#3b6ef8','#10c98e','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#84cc16','#f97316'];

// Fix: always compare as numbers
const isCorrect = (j, q_correctIndex) => q_correctIndex !== undefined && q_correctIndex !== null && Number(j) === Number(q_correctIndex);

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .qz * { box-sizing:border-box; }
  .qz { font-family:'Sora',sans-serif; color:#1a1d2e; animation:qzFade .4s ease both; }
  @keyframes qzFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .qz-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .qz-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .qz-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue   { background:#3b6ef8; color:white; } .btn-blue:hover   { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); }
  .btn-green  { background:#10c98e; color:white; } .btn-green:hover  { background:#0db87e; }
  .btn-purple { background:#8b5cf6; color:white; } .btn-purple:hover { background:#7c3aed; }
  .btn-amber  { background:#f59e0b; color:white; } .btn-amber:hover  { background:#d97706; }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover  { background:#e4e7f0; }
  .btn-red    { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:5px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .breadcrumb { display:flex; align-items:center; gap:6px; margin-bottom:1.2rem; flex-wrap:wrap; }
  .bc-item { font-size:.78rem; font-weight:600; color:#9094a8; cursor:pointer; transition:color .15s; }
  .bc-item:hover { color:#8b5cf6; }
  .bc-item.active { color:#0f1123; cursor:default; }
  .bc-sep { color:#d1d5db; font-size:.82rem; }
  .card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; margin-bottom:1rem; }
  .nav-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .nav-card:hover { border-color:#8b5cf6; box-shadow:0 4px 16px rgba(139,92,246,.1); transform:translateY(-1px); }
  .nc-name  { font-size:.95rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
  .nc-sub   { font-size:.72rem; color:#9094a8; }
  .nc-arrow { display:flex; align-items:center; justify-content:space-between; margin-top:10px; }
  .nc-arrow svg { width:15px; height:15px; stroke:#8b5cf6; stroke-width:2; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .quiz-row { display:flex; align-items:center; justify-content:space-between; padding:13px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; }
  .quiz-row:last-child { border-bottom:none; }
  .quiz-row:hover { background:#fafbff; }
  .qr-title  { font-size:.84rem; font-weight:700; color:#0f1123; margin-bottom:2px; }
  .qr-meta   { font-size:.72rem; color:#9094a8; }
  .qr-actions { display:flex; gap:6px; align-items:center; flex-shrink:0; flex-wrap:wrap; }
  .pill { font-size:.66rem; font-weight:600; padding:2px 8px; border-radius:99px; white-space:nowrap; display:inline-flex; }
  .pill-purple { background:#f3f0ff; color:#8b5cf6; }
  .pill-blue   { background:#eef2ff; color:#3b6ef8; }
  .pill-green  { background:#ecfdf5; color:#10c98e; }
  .pill-amber  { background:#fffbeb; color:#f59e0b; }
  .pill-red    { background:#fef2f2; color:#ef4444; }
  .pill-gray   { background:#f3f4f6; color:#6b7280; }
  .pill-teal   { background:#f0fdfa; color:#0d9488; }
  .q-list { padding:0 18px 18px; }
  .q-item { background:#f8f9fc; border-radius:12px; padding:14px 16px; margin-bottom:10px; border:1px solid #e8ebf4; }
  .q-num  { font-size:.68rem; font-weight:700; color:#8b5cf6; text-transform:uppercase; letter-spacing:.06em; margin-bottom:6px; }
  .q-text { font-size:.84rem; font-weight:600; color:#0f1123; margin-bottom:10px; line-height:1.5; }
  .q-options { display:grid; grid-template-columns:1fr 1fr; gap:6px; }
  .q-opt { font-size:.78rem; padding:8px 10px; border-radius:8px; background:white; border:1.5px solid #e0e4f0; display:flex; align-items:center; gap:7px; transition:all .15s; }
  .q-opt.correct { border-color:#10c98e !important; background:#ecfdf5 !important; color:#065f46 !important; font-weight:700 !important; }
  .q-opt-letter { width:20px; height:20px; border-radius:99px; background:#eef2ff; color:#3b6ef8; font-size:.66rem; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .q-opt.correct .q-opt-letter { background:#10c98e !important; color:white !important; }
  .q-explanation { margin-top:8px; font-size:.72rem; color:#6b7194; background:#f0f9ff; border-radius:6px; padding:5px 9px; border-left:2px solid #3b6ef8; }
  .results-table { width:100%; border-collapse:collapse; }
  .results-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .results-table th { padding:9px 14px; text-align:center !important; font-size:.63rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .results-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .results-table tbody tr:last-child { border-bottom:none; }
  .results-table tbody tr:hover { background:#fafbff; }
  .results-table td { padding:10px 14px; font-size:.79rem; vertical-align:middle; text-align:center !important; }
  .score-bar  { height:8px; background:#f0f2f7; border-radius:99px; overflow:hidden; width:80px; }
  .score-fill { height:100%; border-radius:99px; }
  /* Mobile cards for quiz results table */
  .results-cards { display:none; padding:10px; }
  .res-card { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .res-card-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .res-name { font-weight:700; font-size:.84rem; color:#0f1123; }
  .res-roll { font-size:.7rem; color:#9094a8; font-family:'JetBrains Mono',monospace; margin-top:1px; }
  .res-row { display:flex; align-items:center; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .res-row:last-child { border-bottom:none; }
  .res-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.full { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-label .req { color:#ef4444; margin-left:2px; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-textarea { resize:vertical; min-height:100px; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:#8b5cf6; background:white; box-shadow:0 0 0 3px rgba(139,92,246,.08); }
  .mq-question { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:14px 16px; margin-bottom:12px; }
  .mq-q-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
  .mq-q-num  { font-size:.72rem; font-weight:700; color:#8b5cf6; }
  .mq-options { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:8px; }
  .mq-opt-row { display:flex; align-items:center; gap:8px; }
  .mq-opt-letter { width:22px; height:22px; border-radius:99px; background:#eef2ff; color:#3b6ef8; font-size:.66rem; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .mq-opt-letter.correct { background:#10c98e; color:white; }
  .mq-correct-row { display:flex; gap:8px; flex-wrap:wrap; margin-top:8px; }
  .mq-correct-btn { padding:5px 12px; border-radius:8px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; transition:all .12s; color:#6b7194; }
  .mq-correct-btn.active { border-color:#10c98e; background:#ecfdf5; color:#065f46; }
  .drop-zone { border:2px dashed #d0d5e8; border-radius:12px; padding:28px 20px; text-align:center; cursor:pointer; transition:all .15s; background:#fafbfc; }
  .drop-zone:hover,.drop-zone.drag-over { border-color:#8b5cf6; background:#faf5ff; }
  .diff-row { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
  .diff-btn { padding:8px; border-radius:10px; border:2px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .15s; text-align:center; }
  .diff-btn.easy.active   { border-color:#10c98e; background:#ecfdf5; color:#065f46; }
  .diff-btn.medium.active { border-color:#f59e0b; background:#fffbeb; color:#92400e; }
  .diff-btn.hard.active   { border-color:#ef4444; background:#fef2f2; color:#991b1b; }
  .modal-backdrop { position:fixed; inset:0; background:rgba(10,12,28,.6); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:99999; padding:20px; }
  .modal-box { background:white; border-radius:20px; width:100%; max-width:520px; border:1px solid #e8ebf4; box-shadow:0 24px 64px rgba(0,0,0,.22); overflow:hidden; display:flex; flex-direction:column; max-height:90vh; }
  .modal-box.wide { max-width:700px; }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f2f8; flex-shrink:0; gap:8px; }
  .modal-title { font-size:.92rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:8px; border:1px solid #e8ebf4; background:#f8f9fc; display:flex; align-items:center; justify-content:center; cursor:pointer; color:#9094a8; flex-shrink:0; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-body  { padding:18px 20px; overflow-y:auto; flex:1; }
  .modal-footer { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f2f8; flex-shrink:0; flex-wrap:wrap; }
  .gen-progress { text-align:center; padding:32px 20px; }
  .gen-progress-icon { font-size:2.5rem; margin-bottom:12px; animation:pulse 1s ease infinite; }
  @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }
  .progress-bar  { height:6px; background:#f0f2f7; border-radius:99px; overflow:hidden; margin-top:16px; }
  .progress-fill { height:100%; background:linear-gradient(90deg,#8b5cf6,#3b6ef8); border-radius:99px; animation:progAni 2s ease infinite; }
  @keyframes progAni { 0%{width:10%} 50%{width:80%} 100%{width:95%} }
  .regen-box { background:#f8f9fc; border:1px solid #e8ebf4; border-radius:12px; padding:14px 16px; margin-bottom:14px; }
  .regen-box-head { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px; }
  .answer-key-banner { background:linear-gradient(135deg,#ecfdf5,#f0fdf4); border:1.5px solid #10c98e; border-radius:12px; padding:12px 16px; margin-bottom:14px; display:flex; align-items:center; gap:8px; font-size:.82rem; font-weight:700; color:#065f46; }
  .answer-key-note { padding:8px 18px; background:#f0fdf4; border-bottom:1px solid #a7f3d0; font-size:.72rem; color:#10c98e; font-weight:700; display:flex; align-items:center; gap:6px; }
  .empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#8b5cf6; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  @media (max-width:768px) {
    .qz-title { font-size:1.1rem; }
    .card-grid { grid-template-columns:1fr 1fr; }
    .q-options { grid-template-columns:1fr; }
    .mq-options { grid-template-columns:1fr; }
    .results-wrap { display:none !important; }
    .results-cards { display:block; }
    .modal-body .form-grid { grid-template-columns:1fr; }
    .modal-body .form-field.full { grid-column:1; }
    .modal-footer { justify-content:stretch; }
    .modal-footer .btn { flex:1; justify-content:center; }
    .diff-btn { padding:6px 4px; font-size:.72rem; }
  }
`;

const TeacherQuiz = () => {
  const [navStep,       setNavStep]       = useState('grades');
  const [activeClass,   setActiveClass]   = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const [activeSubject, setActiveSubject] = useState('');
  const [classes,         setClasses]         = useState([]);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [quizzes,         setQuizzes]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [activeQuiz,      setActiveQuiz]      = useState(null);
  const [results,         setResults]         = useState(null);

  const [aiStep,        setAiStep]        = useState(1);
  const [docText,       setDocText]       = useState('');
  const [docName,       setDocName]       = useState('');
  const [genForm,       setGenForm]       = useState({ title:'', difficulty:'medium', numQuestions:'10', instructions:'' });
  const [generating,    setGenerating]    = useState(false);
  const [generatedQuiz, setGeneratedQuiz] = useState(null);

  const [showRegen,        setShowRegen]        = useState(false);
  const [regenInstructions,setRegenInstructions]= useState('');
  const [regenerating,     setRegenerating]     = useState(false);

  const [manualForm,     setManualForm]     = useState({ title:'', difficulty:'medium' });
  const [manualQuestions,setManualQuestions]= useState([emptyQuestion()]);
  const [savingManual,   setSavingManual]   = useState(false);

  const [assignModal, setAssignModal] = useState(null);
  const [assignForm,  setAssignForm]  = useState({ classId:'', section:'', dueDate:'', academicYear: curYear(), assessmentTypeId:'', maxMarks:'' });
  const [assigning,   setAssigning]   = useState(false);

  const [closeModal, setCloseModal] = useState(null);
  const [closeForm,  setCloseForm]  = useState({ assessmentTypeId:'', academicYear: curYear(), termPeriodId:'', countsTowardTerm:true });
  const [isTermMode,  setIsTermMode]  = useState(false);
  const [termPeriods, setTermPeriods] = useState([]);
  const [closing,    setClosing]    = useState(false);

  const fileRef = useRef(null);

  function emptyQuestion() {
    return { question:'', options:['','','',''], correctIndex:null, explanation:'' };
  }

  useEffect(() => {
    api.get('/teacher/assessment-mode')
      .then(({ data }) => data?.assessmentMode || 'assessment')
      .catch(() => 'assessment')
      .then(mode => {
        setIsTermMode(mode === 'term');
        return Promise.all([
          api.get('/teacher/classes'),
          api.get(`/teacher/assessment-types${mode === 'term' ? '?kind=term_component' : ''}`),
          api.get('/teacher/profile'),
          mode === 'term' ? api.get('/teacher/term-periods').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
      }).then(([c, t, p, tp]) => {
        setClasses(c.data?.classes || []);
        setAssessmentTypes(t.data || []);
        setTeacherSubjects(p.data?.subjects || []);
        setTermPeriods(tp?.data || []);
      }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fetchQuizzes = async (classId, section, subject) => {
    try { setLoading(true); const { data } = await api.get('/teacher/quizzes', { params: { classId, section, subject } }); setQuizzes(data); }
    catch { toast.error('Failed to load quizzes'); } finally { setLoading(false); }
  };

  const goGrades   = () => { setNavStep('grades'); setActiveClass(null); setActiveSection(''); setActiveSubject(''); };
  const goSections = (cls) => { setActiveClass(cls); setActiveSection(''); setNavStep('sections'); };
  const goSubjects = (sec) => { setActiveSection(sec); setNavStep('subjects'); };
  const goList     = (subj) => { setActiveSubject(subj); setNavStep('list'); fetchQuizzes(activeClass._id, activeSection, subj); };

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf','doc','docx','txt'].includes(ext)) { toast.error('Please upload PDF, Word, or .txt file'); return; }
    if (file.size > 10*1024*1024) { toast.error('File too large. Max 10MB.'); return; }
    setDocName(file.name);
    if (ext === 'txt') { const text = await file.text(); setDocText(text); setGenForm(f => ({...f, title: file.name.replace(/\.[^/.]+$/,'').replace(/[-_]/g,' ').trim()})); setAiStep(2); return; }
    try {
      toast.loading('Reading document…', { id: 'extract' });
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/teacher/quizzes/extract-text', fd, { headers: { 'Content-Type':'multipart/form-data' } });
      toast.dismiss('extract');
      if (!data.text?.trim()) { toast.error('No text found in document.'); return; }
      setDocText(data.text); setGenForm(f => ({...f, title: file.name.replace(/\.[^/.]+$/,'').replace(/[-_]/g,' ').trim()}));
      toast.success('Document read!'); setAiStep(2);
    } catch (err) { toast.dismiss('extract'); toast.error(err.response?.data?.message || 'Could not read file.'); }
  };

  const handleGenerate = async () => {
    if (!docText.trim()) { toast.error('No document text found'); return; }
    if (!genForm.title.trim()) { toast.error('Enter a quiz title'); return; }
    setAiStep(3); setGenerating(true);
    try {
      const { data } = await api.post('/teacher/quizzes/generate', {
        title: genForm.title, subject: activeSubject, difficulty: genForm.difficulty,
        numQuestions: Number(genForm.numQuestions) || 10, sourceDocText: docText, sourceDocName: docName,
        instructions: genForm.instructions || undefined,
      });
      setGeneratedQuiz(data); setActiveQuiz(data); setAiStep(4);
      setShowRegen(false); setRegenInstructions('');
    } catch (err) { toast.error(err.response?.data?.message || 'Generation failed'); setAiStep(2); }
    finally { setGenerating(false); }
  };

  const handleRegenerate = async () => {
    if (!docText.trim()) {
      toast.error('Document not loaded. Please go to AI Quiz Creator and re-upload your document.');
      setShowRegen(false);
      return;
    }
    const wasInPreview = navStep === 'preview';
    setShowRegen(false);
    if (!wasInPreview) setAiStep(3);
    setRegenerating(true);
    try {
      const { data } = await api.post('/teacher/quizzes/generate', {
        title: genForm.title || activeQuiz?.title || 'Quiz',
        subject: activeSubject,
        difficulty: genForm.difficulty || activeQuiz?.difficulty || 'medium',
        numQuestions: Number(genForm.numQuestions) || activeQuiz?.questions?.length || 10,
        sourceDocText: docText, sourceDocName: docName,
        instructions: regenInstructions || genForm.instructions || undefined,
      });
      setGeneratedQuiz(data); setActiveQuiz(data);
      setRegenInstructions('');
      toast.success('Quiz regenerated!');
      if (!wasInPreview) setAiStep(4);
      // If we were in preview, stay in preview with new quiz
    } catch (err) {
      toast.error(err.response?.data?.message || 'Regeneration failed');
      if (!wasInPreview) setAiStep(4);
    }
    finally { setRegenerating(false); }
  };

  const addQuestion    = () => setManualQuestions(q => [...q, emptyQuestion()]);
  const removeQuestion = (i) => setManualQuestions(q => q.filter((_,idx) => idx !== i));
  const updateQuestion = (i, field, value) => setManualQuestions(prev => { const n=[...prev]; n[i]={...n[i],[field]:value}; return n; });
  const updateOption   = (qi, oi, value)   => setManualQuestions(prev => { const n=[...prev]; const q={...n[qi]}; q.options=[...q.options]; q.options[oi]=value; n[qi]=q; return n; });

  const handleSaveManual = async () => {
    if (!manualForm.title.trim()) { toast.error('Quiz title is required'); return; }
    for (let i=0; i<manualQuestions.length; i++) {
      const q = manualQuestions[i];
      if (!q.question.trim()) { toast.error(`Question ${i+1}: question text is required`); return; }
      if (q.options.some(o => !o.trim())) { toast.error(`Question ${i+1}: all options must have text`); return; }
      if (q.correctIndex === null) { toast.error(`Question ${i+1}: select the correct answer`); return; }
    }
    try {
      setSavingManual(true);
      const { data } = await api.post('/teacher/quizzes/manual', { title: manualForm.title, subject: activeSubject, difficulty: manualForm.difficulty, questions: manualQuestions });
      toast.success('Manual quiz created!'); setActiveQuiz(data); setNavStep('preview');
      setManualForm({ title:'', difficulty:'medium' }); setManualQuestions([emptyQuestion()]);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to create quiz'); }
    finally { setSavingManual(false); }
  };

  // Stored term settings on a quiz -> form values (termPeriodId may come back populated).
  const quizTermForm = (quiz) => ({
    termPeriodId: quiz?.termPeriodId ? String(quiz.termPeriodId._id || quiz.termPeriodId) : '',
    countsTowardTerm: quiz?.countsTowardTerm !== false,
  });
  // Active terms, plus the currently selected one even if it was deactivated.
  const termOptions = (selId) => termPeriods.filter(t => t.active || String(t._id) === String(selId || ''));

  const openAssign = (quiz) => {
    setAssignModal(quiz);
    setAssignForm({ classId: activeClass?._id||'', section: activeSection||'', dueDate:'', academicYear: curYear(), assessmentTypeId:'', maxMarks: String(quiz.questions?.length||10) });
  };

  const handleAssign = async () => {
    if (!assignForm.classId || !assignForm.section) { toast.error('Select class and section'); return; }
    setAssigning(true);
    try {
      const body = { classId: assignForm.classId, section: assignForm.section, subject: activeSubject || assignModal.subject || '', dueDate: assignForm.dueDate || undefined, academicYear: assignForm.academicYear, assessmentTypeId: assignForm.assessmentTypeId || undefined, maxMarks: Number(assignForm.maxMarks) || assignModal.questions?.length || 10 };
      await api.put(`/teacher/quizzes/${assignModal._id}/assign`, body);
      toast.success('Quiz assigned to class!'); setAssignModal(null);
      fetchQuizzes(activeClass._id, activeSection, activeSubject);
      if (activeQuiz?._id === assignModal._id) { const { data } = await api.get(`/teacher/quizzes/${assignModal._id}`); setActiveQuiz(data); }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setAssigning(false); }
  };

  const getSections = (classId) => classes.find(c => c._id === classId)?.sections || [];

  const openResults = async (quiz) => {
    try { const { data } = await api.get(`/teacher/quizzes/${quiz._id}/results`); setResults(data); setActiveQuiz(quiz); setNavStep('results'); }
    catch { toast.error('Failed to load results'); }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      const body = { assessmentTypeId: closeForm.assessmentTypeId, academicYear: closeForm.academicYear };
      if (isTermMode) Object.assign(body, termPayload(closeForm));
      await api.post(`/teacher/quizzes/${closeModal._id}/close`, body);
      toast.success('Quiz closed and scores pushed to Grade Report!'); setCloseModal(null);
      fetchQuizzes(activeClass._id, activeSection, activeSubject);
      if (navStep === 'results') { const r = await api.get(`/teacher/quizzes/${closeModal._id}/results`); setResults(r.data); }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setClosing(false); }
  };

  // ── Question renderer with correct answer highlighting ──────────────────────
  const QuestionList = ({ questions }) => (
    <div className="q-list">
      {questions?.map((q, i) => (
        <div key={q._id||i} className="q-item">
          <div className="q-num">Question {i+1}</div>
          <div className="q-text">{q.question}</div>
          <div className="q-options">
            {q.options?.map((opt, j) => (
              <div key={j} className={`q-opt${isCorrect(j, q.correctIndex ?? q.correct_index) ? ' correct' : ''}`}
                style={isCorrect(j, q.correctIndex ?? q.correct_index) ? {borderColor:'#10c98e',background:'#ecfdf5',color:'#065f46',fontWeight:700} : {}}>
<span className="q-opt-letter" style={isCorrect(j, q.correctIndex ?? q.correct_index) ? {background:'#10c98e',color:'white'} : {}}>{String.fromCharCode(65+j)}</span>
                {opt}
{isCorrect(j, q.correctIndex ?? q.correct_index) && <span style={{marginLeft:'auto',fontSize:'.65rem',fontWeight:700,color:'#10c98e'}}>✓</span>}
              </div>
            ))}
          </div>
          {q.explanation && <div className="q-explanation">💡 {q.explanation}</div>}
        </div>
      ))}
    </div>
  );

  const Breadcrumb = () => (
    <div className="breadcrumb">
      <span className="bc-item" onClick={goGrades}>AI Quizzes</span>
      {activeClass && <><span className="bc-sep">›</span><span className={`bc-item ${navStep==='sections'?'active':''}`} onClick={() => { setNavStep('sections'); setActiveSection(''); setActiveSubject(''); }}>{activeClass.name}</span></>}
      {activeSection && <><span className="bc-sep">›</span><span className={`bc-item ${navStep==='subjects'?'active':''}`} onClick={() => { setNavStep('subjects'); setActiveSubject(''); }}>Section {activeSection}</span></>}
      {activeSubject && <><span className="bc-sep">›</span><span className={`bc-item ${['list','create-ai','create-manual'].includes(navStep)?'active':''}`} onClick={() => { if (!['preview','results'].includes(navStep)) return; setNavStep('list'); }}>{activeSubject}</span></>}
      {navStep === 'preview'       && <><span className="bc-sep">›</span><span className="bc-item active">Preview</span></>}
      {navStep === 'results'       && <><span className="bc-sep">›</span><span className="bc-item active">Results</span></>}
      {navStep === 'create-ai'     && <><span className="bc-sep">›</span><span className="bc-item active">Create AI Quiz</span></>}
      {navStep === 'create-manual' && <><span className="bc-sep">›</span><span className="bc-item active">Create Manual Quiz</span></>}
    </div>
  );

  const AssignModal = () => assignModal && ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={() => setAssignModal(null)}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-head"><div className="modal-title">Assign Quiz to Class</div><button className="modal-close" onClick={() => setAssignModal(null)}><XMarkIcon /></button></div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-field"><label className="form-label">Class <span className="req">*</span></label><select className="form-select" value={assignForm.classId} onChange={e => setAssignForm({...assignForm, classId:e.target.value, section:''})}><option value="">Select class…</option>{classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}</select></div>
            <div className="form-field"><label className="form-label">Section <span className="req">*</span></label><select className="form-select" value={assignForm.section} onChange={e => setAssignForm({...assignForm, section:e.target.value})} disabled={!assignForm.classId}><option value="">Select section…</option>{getSections(assignForm.classId).map(s => <option key={s} value={s}>Section {s}</option>)}</select></div>
            <div className="form-field"><label className="form-label">Due Date</label><input type="date" className="form-input" value={assignForm.dueDate} onChange={e => setAssignForm({...assignForm, dueDate:e.target.value})} /></div>
            <div className="form-field"><label className="form-label">Max Marks</label><input type="number" className="form-input" value={assignForm.maxMarks} onChange={e => setAssignForm({...assignForm, maxMarks:e.target.value})} /></div>
            <div className="form-field full"><label className="form-label">{isTermMode ? 'Component' : 'Assessment Type'}</label><select className="form-select" value={assignForm.assessmentTypeId} onChange={e => setAssignForm({...assignForm, assessmentTypeId:e.target.value})}><option value="">Select type…</option>{assessmentTypes.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}</select></div>
            <div className="form-field full"><label className="form-label">Academic Year</label><input type="text" className="form-input" value={assignForm.academicYear} onChange={e => setAssignForm({...assignForm, academicYear:e.target.value})} placeholder="2025-2026" /></div>
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setAssignModal(null)}>Cancel</button><button className="btn btn-blue" onClick={handleAssign} disabled={assigning}>{assigning ? 'Assigning…' : 'Assign Quiz'}</button></div>
      </div>
    </div>
  , document.body);

  // ── RESULTS VIEW ─────────────────────────────────────────────────────────────
  if (navStep === 'results' && results) {
    const { quiz, attempts, notAttempted } = results;
    const avg = attempts.length ? Math.round(attempts.reduce((s,a) => s+a.percentage, 0) / attempts.length) : 0;
    return (
      <div className="qz"><style>{style}</style>
        <Breadcrumb />
        <div className="qz-header">
          <div><div className="qz-title">{quiz.title} — Results</div><div className="qz-sub">{quiz.class?.name} · Section {quiz.section} · {attempts.length} submitted · Avg {avg}%</div></div>
          {quiz.status === 'assigned' && <button className="btn btn-green" onClick={() => { setCloseModal(quiz); setCloseForm({ assessmentTypeId: quiz.assessmentType||'', academicYear: curYear(), ...quizTermForm(quiz) }); }}><LockClosedIcon />Close & Push to Grade Report</button>}
        </div>
        <div className="panel">
          <div className="panel-head"><div className="panel-title">Submitted ({attempts.length})</div></div>
          {attempts.length === 0 ? <div className="empty">No attempts yet.</div> : (
            <>
              <div className="results-wrap" style={{overflowX:'auto'}}>
                <table className="results-table">
                  <thead><tr><th>#</th><th>Student</th><th>Score</th><th>%</th><th>Time</th></tr></thead>
                  <tbody>
                    {attempts.map((a,i) => {
                      const pct = a.percentage; const color = pct>=80?'#10c98e':pct>=50?'#f59e0b':'#ef4444';
                      return (
                        <tr key={a._id}>
                          <td style={{color:'#9094a8',fontFamily:'JetBrains Mono',fontSize:'.72rem'}}>{i+1}</td>
                          <td><div style={{fontWeight:600,fontSize:'.82rem'}}>{a.student?.user?.name}</div><div style={{fontSize:'.68rem',color:'#9094a8'}}>Roll {a.student?.rollNo}</div></td>
                          <td><span style={{fontFamily:'JetBrains Mono',fontWeight:700}}>{a.score}/{a.totalQuestions}</span></td>
                          <td><div style={{display:'flex',alignItems:'center',gap:8}}><div className="score-bar"><div className="score-fill" style={{width:`${pct}%`,background:color}}/></div><span style={{fontFamily:'JetBrains Mono',fontSize:'.76rem',fontWeight:700,color}}>{pct}%</span></div></td>
                          <td style={{fontSize:'.74rem',color:'#6b7194'}}>{a.timeTaken ? `${Math.floor(a.timeTaken/60)}m ${a.timeTaken%60}s` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="results-cards">
                {attempts.map((a,i) => {
                  const pct = a.percentage; const color = pct>=80?'#10c98e':pct>=50?'#f59e0b':'#ef4444';
                  return (
                    <div key={a._id} className="res-card">
                      <div className="res-card-top">
                        <div>
                          <div className="res-name">#{i+1} · {a.student?.user?.name}</div>
                          <div className="res-roll">Roll {a.student?.rollNo}</div>
                        </div>
                      </div>
                      <div className="res-row"><span className="res-label">Score</span><span style={{fontFamily:'JetBrains Mono',fontWeight:700}}>{a.score}/{a.totalQuestions}</span></div>
                      <div className="res-row">
                        <span className="res-label">Percentage</span>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div className="score-bar"><div className="score-fill" style={{width:`${pct}%`,background:color}}/></div>
                          <span style={{fontFamily:'JetBrains Mono',fontSize:'.76rem',fontWeight:700,color}}>{pct}%</span>
                        </div>
                      </div>
                      <div className="res-row"><span className="res-label">Time</span><span>{a.timeTaken ? `${Math.floor(a.timeTaken/60)}m ${a.timeTaken%60}s` : '—'}</span></div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        {notAttempted.length > 0 && (
          <div className="panel">
            <div className="panel-head"><div className="panel-title" style={{color:'#ef4444'}}>Not Attempted ({notAttempted.length})</div></div>
            <div style={{padding:'10px 18px',display:'flex',gap:8,flexWrap:'wrap'}}>{notAttempted.map(s => <span key={s._id} style={{background:'#fef2f2',color:'#ef4444',fontSize:'.74rem',padding:'3px 10px',borderRadius:99,fontWeight:600}}>{s.user?.name}</span>)}</div>
          </div>
        )}
        {closeModal && ReactDOM.createPortal(
          <div className="modal-backdrop" onClick={() => setCloseModal(null)}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
              <div className="modal-head"><div className="modal-title">Close Quiz & Push to Grade Report</div><button className="modal-close" onClick={() => setCloseModal(null)}><XMarkIcon /></button></div>
              <div className="modal-body">
                <div style={{background:'#ecfdf5',border:'1px solid #a7f3d0',borderRadius:10,padding:'10px 12px',fontSize:'.78rem',color:'#065f46',marginBottom:14}}>✓ Scores will be added to Grade Report under <strong>{quiz.subject}</strong> for {quiz.class?.name} Section {quiz.section}.</div>
                <div className="form-field" style={{marginBottom:13}}><label className="form-label">Assessment Type</label><select className="form-select" value={closeForm.assessmentTypeId} onChange={e => setCloseForm({...closeForm, assessmentTypeId:e.target.value})}><option value="">Select type…</option>{assessmentTypes.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}</select></div>
                <div className="form-field"><label className="form-label">Academic Year</label><input type="text" className="form-input" value={closeForm.academicYear} onChange={e => setCloseForm({...closeForm, academicYear:e.target.value})} placeholder="2025-2026" /></div>
                {isTermMode && (
                  <div style={{marginTop:13}}>
                    <TermSettingsFields value={closeForm} onChange={setCloseForm} terms={termOptions(closeForm.termPeriodId)} />
                  </div>
                )}
              </div>
              <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setCloseModal(null)}>Cancel</button><button className="btn btn-green" onClick={handleClose} disabled={closing}><CheckCircleIcon />{closing ? 'Closing…' : 'Close & Push Marks'}</button></div>
            </div>
          </div>
        , document.body)}
        <AssignModal />
      </div>
    );
  }

  // ── PREVIEW VIEW ─────────────────────────────────────────────────────────────
  if (navStep === 'preview' && activeQuiz) {
    return (
      <div className="qz"><style>{style}</style>
        <Breadcrumb />
        <div className="qz-header">
          <div>
            <div className="qz-title">{activeQuiz.title}</div>
            <div className="qz-sub">{activeQuiz.questions?.length} questions · {DIFF_LABELS[activeQuiz.difficulty]}{activeQuiz.quizType === 'manual' ? ' · ✍️ Manual' : ' · 🤖 AI Generated'}</div>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {activeQuiz.status === 'draft' && <button className="btn btn-blue" onClick={() => openAssign(activeQuiz)}>Assign to Class</button>}
            {activeQuiz.status === 'assigned' && <button className="btn btn-green" onClick={() => openResults(activeQuiz)}>View Results</button>}
          </div>
        </div>

        {/* Answer Key Banner */}
        <div className="answer-key-banner">
          <CheckCircleIcon style={{width:20,height:20,flexShrink:0,color:'#10c98e'}} />
          🔑 Answer Key View — Correct answers highlighted in green below. Students do not see this.
        </div>

        {activeQuiz.status === 'assigned' && (
          <div style={{background:'#eef2ff',border:'1px solid #c7d2fe',borderRadius:12,padding:'10px 16px',marginBottom:14,fontSize:'.78rem',color:'#3730a3',fontWeight:600}}>
            ✓ Assigned to {activeQuiz.class?.name} Section {activeQuiz.section}{activeQuiz.dueDate ? ` · Due: ${fmtDate(activeQuiz.dueDate)}` : ''}
          </div>
        )}

        {/* Regenerate box — only for AI quizzes in draft */}
        {activeQuiz.quizType !== 'manual' && activeQuiz.status === 'draft' && (
          <div className="regen-box">
            <div className="regen-box-head">
              <div>
                <div style={{fontSize:'.82rem',fontWeight:700,color:'#4a5075'}}>🔄 Regenerate this quiz with new instructions</div>
                <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:2}}>
                  {docText ? 'Document still loaded — give new instructions and regenerate' : 'Re-upload your document to regenerate with new instructions'}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowRegen(r => !r)}>
                <ArrowPathIcon />{showRegen ? 'Hide' : 'Regenerate'}
              </button>
            </div>
            {showRegen && (
              <div style={{marginTop:12}}>
                {!docText ? (
                  <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:9,padding:'10px 12px',fontSize:'.76rem',color:'#92400e',marginBottom:10}}>
                    ⚠️ Document text not available. You need to go back to AI Quiz Creator and re-upload your document.
                    <div style={{marginTop:8}}>
                      <button className="btn btn-ghost btn-sm" onClick={() => {
                        setGenForm({title: activeQuiz.title||'', difficulty: activeQuiz.difficulty||'medium', numQuestions: String(activeQuiz.questions?.length||10), instructions:''});
                        setAiStep(1); setGeneratedQuiz(null); setShowRegen(false); setRegenInstructions('');
                        setNavStep('create-ai');
                      }}><ArrowPathIcon />Go to AI Quiz Creator</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="form-field" style={{marginBottom:10}}>
                      <label className="form-label">New Instructions <span style={{fontWeight:400,textTransform:'none',fontSize:'.68rem',color:'#b0b5cc'}}>(optional)</span></label>
                      <textarea className="form-textarea" style={{minHeight:80}} value={regenInstructions}
                        onChange={e => setRegenInstructions(e.target.value)}
                        placeholder="e.g. Make questions harder, focus on chapter 3, avoid true/false style, add more application questions…" />
                    </div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setShowRegen(false); setRegenInstructions(''); }}>Cancel</button>
                      <button className="btn btn-purple" onClick={handleRegenerate} disabled={regenerating}>
                        <ArrowPathIcon />{regenerating ? 'Regenerating…' : 'Regenerate Quiz'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="panel">
          <div className="panel-head"><div className="panel-title">Questions ({activeQuiz.questions?.length})</div></div>
          <div className="answer-key-note">🟢 Green = Correct Answer &nbsp;·&nbsp; Teacher-only view</div>
          <QuestionList questions={activeQuiz.questions} />
        </div>
        <AssignModal />
      </div>
    );
  }

  // ── CREATE MANUAL ────────────────────────────────────────────────────────────
  if (navStep === 'create-manual') {
    return (
      <div className="qz"><style>{style}</style>
        <Breadcrumb />
        <div className="qz-header"><div><div className="qz-title">Create Manual Quiz</div><div className="qz-sub">{activeSubject} · {activeClass?.name} · Section {activeSection}</div></div></div>
        <div className="panel">
          <div className="panel-head"><div className="panel-title">Quiz Details</div></div>
          <div style={{padding:'18px 20px'}}>
            <div className="form-grid">
              <div className="form-field full"><label className="form-label">Quiz Title <span className="req">*</span></label><input type="text" className="form-input" value={manualForm.title} onChange={e => setManualForm({...manualForm, title:e.target.value})} placeholder="e.g. Chapter 5 Practice Quiz" /></div>
              <div className="form-field full"><label className="form-label">Difficulty Level</label><div className="diff-row">{['easy','medium','hard'].map(d => <button key={d} className={`diff-btn ${d} ${manualForm.difficulty===d?'active':''}`} onClick={() => setManualForm({...manualForm, difficulty:d})}>{DIFF_LABELS[d]}</button>)}</div></div>
            </div>
          </div>
        </div>
        {manualQuestions.map((q, qi) => (
          <div key={qi} className="panel">
            <div className="panel-head"><div className="panel-title" style={{color:'#8b5cf6'}}>Question {qi+1}</div>{manualQuestions.length > 1 && <button className="btn btn-red btn-sm" onClick={() => removeQuestion(qi)}><XMarkIcon />Remove</button>}</div>
            <div style={{padding:'16px 18px'}}>
              <div className="form-field" style={{marginBottom:12}}><label className="form-label">Question Text <span className="req">*</span></label><textarea className="form-textarea" value={q.question} style={{minHeight:70}} onChange={e => updateQuestion(qi, 'question', e.target.value)} placeholder="Enter your question here…" /></div>
              <div className="form-label" style={{marginBottom:8}}>Options <span className="req">*</span></div>
              <div className="mq-options">{q.options.map((opt, oi) => <div key={oi} className="mq-opt-row"><span className={`mq-opt-letter ${isCorrect(oi,q.correctIndex)?'correct':''}`}>{String.fromCharCode(65+oi)}</span><input type="text" className="form-input" value={opt} onChange={e => updateOption(qi, oi, e.target.value)} placeholder={`Option ${String.fromCharCode(65+oi)}`} /></div>)}</div>
              <div style={{marginTop:10}}><div className="form-label" style={{marginBottom:6}}>Correct Answer <span className="req">*</span></div><div className="mq-correct-row">{q.options.map((_, oi) => <button key={oi} className={`mq-correct-btn ${isCorrect(oi,q.correctIndex)?'active':''}`} onClick={() => updateQuestion(qi, 'correctIndex', oi)}>{String.fromCharCode(65+oi)} {q.options[oi] ? `— ${q.options[oi].slice(0,20)}${q.options[oi].length>20?'…':''}` : ''}</button>)}</div></div>
              <div className="form-field" style={{marginTop:10}}><label className="form-label">Explanation (optional)</label><input type="text" className="form-input" value={q.explanation} onChange={e => updateQuestion(qi, 'explanation', e.target.value)} placeholder="Why is this the correct answer?" /></div>
            </div>
          </div>
        ))}
        <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:'1rem'}}>
          <button className="btn btn-ghost" onClick={addQuestion}><PlusIcon />Add Question</button>
          <button className="btn btn-ghost" onClick={() => { setNavStep('list'); setManualForm({title:'',difficulty:'medium'}); setManualQuestions([emptyQuestion()]); }}>Cancel</button>
          <button className="btn btn-purple" style={{marginLeft:'auto'}} onClick={handleSaveManual} disabled={savingManual}><CheckCircleIcon />{savingManual ? 'Saving…' : `Save Quiz (${manualQuestions.length} Q)`}</button>
        </div>
      </div>
    );
  }

  // ── CREATE AI QUIZ ───────────────────────────────────────────────────────────
  if (navStep === 'create-ai') {
    return (
      <div className="qz"><style>{style}</style>
        <Breadcrumb />
        <div className="qz-header"><div><div className="qz-title">Create AI Quiz</div><div className="qz-sub">{activeSubject} · {activeClass?.name} · Section {activeSection}</div></div></div>

        {aiStep === 1 && (
          <div className="panel">
            <div className="panel-head"><div className="panel-title">Step 1 — Upload Document</div></div>
            <div style={{padding:'20px'}}>
              <div className="drop-zone" onClick={() => fileRef.current?.click()} onDragOver={e=>{e.preventDefault();e.currentTarget.classList.add('drag-over');}} onDragLeave={e=>e.currentTarget.classList.remove('drag-over')} onDrop={e=>{e.preventDefault();e.currentTarget.classList.remove('drag-over');handleFile(e.dataTransfer.files[0]);}}>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" style={{display:'none'}} onChange={e=>handleFile(e.target.files[0])} />
                <div style={{fontSize:'2.2rem',marginBottom:8}}>📄</div>
                <div style={{fontWeight:600,fontSize:'.84rem'}}>Tap or drag to upload document</div>
                <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:3}}>PDF · Word (.docx) · Text (.txt) · Max 10MB</div>
              </div>
              <div className="form-field" style={{marginTop:16}}><label className="form-label">Or paste document text directly</label><textarea className="form-textarea" value={docText} onChange={e=>setDocText(e.target.value)} placeholder="Paste your document content here…" style={{minHeight:140}} /></div>
              {docText.trim() && <button className="btn btn-purple" style={{marginTop:8}} onClick={() => setAiStep(2)}><ChevronRightIcon />Continue to Settings</button>}
            </div>
          </div>
        )}

        {aiStep === 2 && (
          <div className="panel">
            <div className="panel-head"><div className="panel-title">Step 2 — Quiz Settings</div><button className="btn btn-ghost btn-sm" onClick={() => setAiStep(1)}>← Change Document</button></div>
            <div style={{padding:'20px'}}>
              {docName && <div style={{background:'#f3f0ff',border:'1px solid #ddd6fe',borderRadius:9,padding:'8px 12px',fontSize:'.76rem',color:'#7c3aed',marginBottom:16}}>📄 {docName} · {Math.round(docText.length/1000)}k chars</div>}
              <div className="form-grid">
                <div className="form-field full"><label className="form-label">Quiz Title <span className="req">*</span></label><input type="text" className="form-input" value={genForm.title} onChange={e=>setGenForm({...genForm,title:e.target.value})} placeholder="e.g. Chapter 5 Quiz" /></div>
                <div className="form-field"><label className="form-label">Number of Questions</label><input type="number" min={1} max={30} className="form-input" value={genForm.numQuestions} onChange={e=>setGenForm({...genForm,numQuestions:e.target.value})} /></div>
                <div className="form-field full"><label className="form-label">Difficulty</label><div className="diff-row">{['easy','medium','hard'].map(d => <button key={d} className={`diff-btn ${d} ${genForm.difficulty===d?'active':''}`} onClick={() => setGenForm({...genForm,difficulty:d})}>{DIFF_LABELS[d]}<div style={{fontSize:'.66rem',fontWeight:400,marginTop:2,opacity:.7}}>{d==='easy'?'Basic recall':d==='medium'?'Application':'Critical thinking'}</div></button>)}</div></div>
                <div className="form-field full">
                  <label className="form-label">Special Instructions <span style={{fontWeight:400,textTransform:'none',fontSize:'.68rem',color:'#b0b5cc'}}>(optional)</span></label>
                  <textarea className="form-textarea" style={{minHeight:70}} value={genForm.instructions} onChange={e=>setGenForm({...genForm,instructions:e.target.value})} placeholder="e.g. Focus on definitions only, avoid calculation questions, include at least 3 questions from chapter 2, make all wrong options plausible…" />
                </div>
              </div>
              <button className="btn btn-purple" style={{marginTop:8}} onClick={handleGenerate} disabled={!genForm.title.trim()}><SparklesIcon />Generate Quiz with AI</button>
            </div>
          </div>
        )}

        {aiStep === 3 && (
          <div className="panel">
            <div className="gen-progress">
              <div className="gen-progress-icon">🤖</div>
              <div style={{fontSize:'.9rem',fontWeight:600,color:'#1a1d2e',marginBottom:6}}>{regenerating ? 'Regenerating your quiz…' : 'AI is generating your quiz…'}</div>
              <div style={{fontSize:'.76rem',color:'#9094a8'}}>Analyzing document · Creating {genForm.numQuestions} questions · {genForm.difficulty} difficulty{(regenInstructions||genForm.instructions)&&<div style={{marginTop:4,color:'#8b5cf6'}}>With special instructions applied</div>}</div>
              <div className="progress-bar"><div className="progress-fill"/></div>
              <div style={{fontSize:'.7rem',color:'#b0b5cc',marginTop:8}}>If the AI is briefly busy, this retries automatically — it can take up to ~30 seconds.</div>
            </div>
          </div>
        )}

        {aiStep === 4 && generatedQuiz && (
          <>
            <div style={{background:'#ecfdf5',border:'1px solid #a7f3d0',borderRadius:12,padding:'12px 16px',marginBottom:14,fontSize:'.82rem',color:'#065f46',fontWeight:600,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <CheckCircleIcon style={{width:18,height:18,flexShrink:0}} />
              ✓ {generatedQuiz.questions?.length} questions created. Review below, then assign to class when ready.
            </div>

            {/* Regenerate box */}
            <div className="regen-box">
              <div className="regen-box-head">
                <div>
                  <div style={{fontSize:'.82rem',fontWeight:700,color:'#4a5075'}}>🔄 Not satisfied? Regenerate with new instructions</div>
                  <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:2}}>Give specific instructions to improve the quiz</div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={()=>setShowRegen(r=>!r)}><ArrowPathIcon />{showRegen ? 'Hide' : 'Regenerate'}</button>
              </div>
              {showRegen && (
                <div style={{marginTop:12}}>
                  <div className="form-field" style={{marginBottom:10}}>
                    <label className="form-label">New Instructions <span style={{fontWeight:400,textTransform:'none',fontSize:'.68rem',color:'#b0b5cc'}}>(optional — leave blank to regenerate same settings)</span></label>
                    <textarea className="form-textarea" style={{minHeight:80}} value={regenInstructions} onChange={e=>setRegenInstructions(e.target.value)} placeholder="e.g. Make the questions harder, focus more on theory, avoid questions about dates, add more application-based questions, replace question 3 with something about photosynthesis…" />
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setShowRegen(false);setRegenInstructions('');}}>Cancel</button>
                    <button className="btn btn-purple" onClick={handleRegenerate} disabled={regenerating}><ArrowPathIcon />{regenerating ? 'Regenerating…' : 'Regenerate Quiz'}</button>
                  </div>
                </div>
              )}
            </div>

            <div className="panel">
              <div className="panel-head">
                <div className="panel-title">Preview — {generatedQuiz.title}</div>
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setActiveQuiz(generatedQuiz); setNavStep('preview'); }}>Full Preview + Answer Key</button>
                  <button className="btn btn-blue" onClick={() => openAssign(generatedQuiz)}>Assign to Class</button>
                </div>
              </div>
              <div className="answer-key-note">🔑 Answer Key: Green = Correct Answer · Teacher-only view</div>
              {/* Show first 3 questions with answer key */}
              <div className="q-list">
                {generatedQuiz.questions?.slice(0,3).map((q,i) => (
                  <div key={i} className="q-item">
                    <div className="q-num">Question {i+1} of {generatedQuiz.questions.length}</div>
                    <div className="q-text">{q.question}</div>
                    <div className="q-options">
                      {q.options?.map((opt,j) => (
                        <div key={j} className={`q-opt${isCorrect(j,q.correctIndex)?' correct':''}`}
                          style={isCorrect(j,q.correctIndex??q.correct_index)?{borderColor:'#10c98e',background:'#ecfdf5',color:'#065f46',fontWeight:700}:{}}>
<span className="q-opt-letter" style={isCorrect(j,q.correctIndex??q.correct_index)?{background:'#10c98e',color:'white'}:{}}>{String.fromCharCode(65+j)}</span>{opt}
{isCorrect(j,q.correctIndex??q.correct_index)&&<span style={{marginLeft:'auto',fontSize:'.65rem',fontWeight:700,color:'#10c98e'}}>✓</span>}
                        </div>
                      ))}
                    </div>
                    {q.explanation && <div className="q-explanation">💡 {q.explanation}</div>}
                  </div>
                ))}
                {generatedQuiz.questions?.length > 3 && <div style={{textAlign:'center',padding:'10px',color:'#9094a8',fontSize:'.78rem'}}>+ {generatedQuiz.questions.length-3} more — click Full Preview to see all</div>}
              </div>
            </div>
            <AssignModal />
          </>
        )}
      </div>
    );
  }

  // ── QUIZ LIST ────────────────────────────────────────────────────────────────
  if (navStep === 'list') return (
    <div className="qz"><style>{style}</style>
      <Breadcrumb />
      <div className="qz-header">
        <div><div className="qz-title">{activeSubject}</div><div className="qz-sub">{activeClass?.name} · Section {activeSection}</div></div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn btn-ghost" onClick={() => { setManualForm({title:'',difficulty:'medium'}); setManualQuestions([emptyQuestion()]); setNavStep('create-manual'); }}><PencilSquareIcon />Manual Quiz</button>
          <button className="btn btn-purple" onClick={() => { setDocText(''); setDocName(''); setGenForm({title:'',difficulty:'medium',numQuestions:'10',instructions:''}); setAiStep(1); setNavStep('create-ai'); }}><SparklesIcon />AI Quiz</button>
        </div>
      </div>
      <div className="panel">
        <div className="panel-head"><div className="panel-title">Quizzes ({quizzes.length})</div></div>
        {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
        : quizzes.length === 0 ? (
          <div className="empty">No quizzes for {activeSubject} yet.<br/><div style={{display:'flex',gap:8,justifyContent:'center',marginTop:12,flexWrap:'wrap'}}><button className="btn btn-ghost btn-sm" onClick={() => { setManualForm({title:'',difficulty:'medium'}); setManualQuestions([emptyQuestion()]); setNavStep('create-manual'); }}><PencilSquareIcon />Manual Quiz</button><button className="btn btn-purple btn-sm" onClick={() => { setDocText(''); setDocName(''); setAiStep(1); setNavStep('create-ai'); }}><SparklesIcon />AI Quiz</button></div></div>
        ) : quizzes.map(q => (
          <div key={q._id} className="quiz-row">
            <div style={{flex:1,minWidth:0}}>
              <div className="qr-title">{q.title}</div>
              <div className="qr-meta">{q.questions?.length} questions{q.dueDate ? ` · Due: ${fmtDate(q.dueDate)}` : ''}{' · '}Created {fmtDate(q.createdAt)}</div>
              {isTermMode && q.status !== 'draft' && q.termPeriodId && (
                <div className="qr-meta">Term: {termPeriods.find(t => String(t._id) === String(q.termPeriodId._id || q.termPeriodId))?.name || 'Term'}{q.countsTowardTerm === false ? ' · not counted' : ''}</div>
              )}
              <div style={{display:'flex',gap:5,flexWrap:'wrap',marginTop:5}}>
                <span className={`pill ${q.status==='draft'?'pill-gray':q.status==='assigned'?'pill-blue':'pill-green'}`}>{q.status}</span>
                <span className={`pill ${q.difficulty==='easy'?'pill-green':q.difficulty==='medium'?'pill-amber':'pill-red'}`}>{DIFF_LABELS[q.difficulty]}</span>
                <span className={`pill ${q.quizType==='manual'?'pill-teal':'pill-purple'}`}>{q.quizType==='manual'?'✍️ Manual':'🤖 AI'}</span>
              </div>
            </div>
            <div className="qr-actions">
              <button className="btn btn-ghost btn-sm" onClick={() => { setActiveQuiz(q); setNavStep('preview'); }}>Preview + Key</button>
              {q.status === 'draft'    && <button className="btn btn-blue btn-sm" onClick={() => openAssign(q)}>Assign</button>}
              {q.status === 'assigned' && <button className="btn btn-green btn-sm" onClick={() => openResults(q)}>Results</button>}
              {q.status === 'closed'   && <button className="btn btn-ghost btn-sm" onClick={() => openResults(q)}>Results</button>}
            </div>
          </div>
        ))}
      </div>
      <AssignModal />
    </div>
  );

  // ── SUBJECTS ─────────────────────────────────────────────────────────────────
  if (navStep === 'subjects' && activeClass) return (
    <div className="qz"><style>{style}</style>
      <Breadcrumb />
      <div className="qz-header"><div><div className="qz-title">Section {activeSection}</div><div className="qz-sub">{activeClass.name} · Select a subject</div></div></div>
      {teacherSubjects.length === 0 ? <div className="empty">No subjects found.</div> : (
        <div className="card-grid">
          {teacherSubjects.map((subj, i) => (
            <div key={subj} className="nav-card" onClick={() => goList(subj)}>
              <div style={{width:32,height:32,borderRadius:9,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.72rem',marginBottom:8}}>{subj.slice(0,2).toUpperCase()}</div>
              <div className="nc-name">{subj}</div>
              <div className="nc-arrow"><span style={{fontSize:'.72rem',color:SUBJ_COLORS[i%SUBJ_COLORS.length],fontWeight:600}}>View quizzes</span><ChevronRightIcon style={{width:15,height:15,stroke:SUBJ_COLORS[i%SUBJ_COLORS.length]}} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── SECTIONS ─────────────────────────────────────────────────────────────────
  if (navStep === 'sections' && activeClass) {
    const secs = activeClass.sections || [];
    return (
      <div className="qz"><style>{style}</style>
        <Breadcrumb />
        <div className="qz-header"><div><div className="qz-title">{activeClass.name}</div><div className="qz-sub">Select a section</div></div></div>
        {secs.length === 0 ? <div className="empty">No sections found.</div> : (
          <div className="card-grid">
            {secs.map((sec, i) => (
              <div key={sec} className="nav-card" onClick={() => goSubjects(sec)}>
                <div style={{width:36,height:36,borderRadius:10,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.9rem',marginBottom:8}}>{sec}</div>
                <div className="nc-name">Section {sec}</div>
                <div className="nc-sub">{teacherSubjects.length} subject{teacherSubjects.length!==1?'s':''}</div>
                <div className="nc-arrow"><span style={{fontSize:'.72rem',color:SUBJ_COLORS[i%SUBJ_COLORS.length],fontWeight:600}}>View subjects</span><ChevronRightIcon style={{width:15,height:15,stroke:SUBJ_COLORS[i%SUBJ_COLORS.length]}} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── GRADE CARDS ──────────────────────────────────────────────────────────────
  return (
    <div className="qz"><style>{style}</style>
      <div className="qz-header"><div><div className="qz-title">AI Quizzes</div><div className="qz-sub">Select a class to view or create quizzes</div></div></div>
      {loading ? <div className="loading"><div className="spinner"/>Loading…</div>
      : classes.length === 0 ? <div className="empty">No classes assigned yet.</div>
      : (
        <div className="card-grid">
          {classes.map((cls, i) => (
            <div key={cls._id} className="nav-card" onClick={() => goSections(cls)}>
              <div style={{width:36,height:36,borderRadius:10,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.8rem',marginBottom:8}}>{cls.name?.replace('Grade ','G').slice(0,3)}</div>
              <div className="nc-name">{cls.name}</div>
              <div className="nc-sub">{cls.sections?.length||0} section{cls.sections?.length!==1?'s':''}</div>
              {teacherSubjects.length > 0 && (
                <div style={{display:'flex',gap:4,flexWrap:'wrap',marginTop:6}}>
                  {teacherSubjects.map(s => <span key={s} style={{fontSize:'.65rem',fontWeight:600,padding:'2px 7px',borderRadius:99,background:'#f3f0ff',color:'#8b5cf6'}}>{s}</span>)}
                </div>
              )}
              <div className="nc-arrow"><span style={{fontSize:'.72rem',color:'#8b5cf6',fontWeight:600}}>View sections</span><ChevronRightIcon style={{width:15,height:15,stroke:'#8b5cf6'}} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherQuiz;